import {mapValueKeys} from '@shared/helpers/object'

import {Delivery} from '@web/actions/deliveryInfo'
import {DeliveryFeeClass, OrderEntry, OrderEntryType} from '@web/core/constants'
import {decodeHtmlEntities as decode} from '@web/helpers/htmlEntities'
import {DeliveryGroupState} from '@web/slices/deliveryGroup/slice'
import {AddressInfo} from '@web/slices/deliveryInfo/slice'

import {
    DeliveryAddress as DeliveryAddressAPI,
    DeliveryFee,
    EasyBookingDeliveryFee,
} from '../../apis/interface/response/interface'
import {FulfilledException} from '../../constants'

/**
 * 해외 배송 여부
 * - 가맹점의 경우, 판매자 별 개인통관고유부호 노출 여부로 해외배송 여부를 판단.
 * - 스마트스토의 경우, 상품단위 별 overseas 값을 이용해 해외배송 여부 판단.
 */
export const isOverseasDelivery = ({
    merchantsByMerchantNo,
    channel,
}: {
    merchantsByMerchantNo: Record<
        number,
        {
            personalCustomsCodeExposed: Maybe<boolean>
        }
    >
    channel: OrderEntryType
}) =>
    channel === OrderEntry.CHECKOUT
        ? Object.values(merchantsByMerchantNo).some((merchant) => merchant.personalCustomsCodeExposed)
        : false

export const getDeliveryException = (deliveryFee: DeliveryFee | EasyBookingDeliveryFee): FulfilledException => {
    return {
        code: 'REMOTE_ADDITIONAL_DELIVERY_FEE_FAILED',
        fallbackMessage:
            deliveryFee.remoteAdditionalDeliveryFeeFailureMessage ??
            '지역별 추가 배송비 확인시 일시적인 오류가 발생했습니다.',
    }
}

export const convertDeliveryAddress = (addressInfo?: Maybe<DeliveryAddressAPI>): Maybe<AddressInfo> => {
    if (!addressInfo) {
        return null
    }

    return {
        hash: addressInfo.addressId,
        addressName: decode(addressInfo.addressName),
        baseAddress: decode(addressInfo.baseAddress),
        detailAddress: decode(addressInfo.detailAddress ?? ''),
        zipCode: addressInfo.zipCode,
        buildingManagementNo: addressInfo.buildingManagementNo,
        locationX: addressInfo.locationX,
        locationY: addressInfo.locationY,
        receiverName: decode(addressInfo.receiverName),
        telNo1: addressInfo.telNo1,
        telNo2: addressInfo.telNo2,
        isPrimaryAddress: addressInfo.primary,
        isRegistered: addressInfo.registered ?? false,
        isRoadNameAddress: addressInfo.road,
        locationType: null,
        locationDescription: null,
        entryMethodType: null,
        entryDescription: null,
    }
}

export function convertDeliveryGroupsByKey(
    deliveryGroupsByKey: DeliveryFee['deliveryGroupsByKey'] | Delivery['deliveryGroupsByKey'],
): DeliveryGroupState['deliveryGroupsByKey'] {
    return mapValueKeys((deliveryGroupItem) => {
        return {
            additionalDeliveryFee: deliveryGroupItem.additionalDeliveryFee,
            deliveryFee: deliveryGroupItem.deliveryFee,
            deliveryFeePayType: deliveryGroupItem.deliveryFeePayType ?? DeliveryFeeClass.FREE,
            deliveryFeeDiscountAmount: null,
            deliveryPolicy: {
                baseFee: deliveryGroupItem.deliveryPolicy.baseFee,
                deliveryArea2ExtraFee: deliveryGroupItem.deliveryPolicy.deliveryArea2ExtraFee,
                deliveryArea3ExtraFee: deliveryGroupItem.deliveryPolicy.deliveryArea3ExtraFee,
                deliveryAreaType: deliveryGroupItem.deliveryPolicy.deliveryAreaType,
                deliveryFeeClassType: deliveryGroupItem.deliveryPolicy.deliveryFeeClassType,
                deliveryFeePayType: deliveryGroupItem.deliveryPolicy.deliveryFeePayType ?? DeliveryFeeClass.FREE,
                deliveryMethodType: deliveryGroupItem.deliveryPolicy.deliveryMethodType,
                freeConditionalAmount: deliveryGroupItem.deliveryPolicy.freeConditionalAmount,
                repeatQuantity: deliveryGroupItem.deliveryPolicy.repeatQuantity,
                secondBaseQuantity: deliveryGroupItem.deliveryPolicy.secondBaseQuantity,
                secondExtraFee: deliveryGroupItem.deliveryPolicy.secondExtraFee,
                thirdBaseQuantity: deliveryGroupItem.deliveryPolicy.thirdBaseQuantity,
                thirdExtraFee: deliveryGroupItem.deliveryPolicy.thirdExtraFee,
                additionalFeeMessage: decode(deliveryGroupItem.deliveryPolicy.additionalFeeMessage),
            },
        }
    }, deliveryGroupsByKey)
}
